import React from 'react'
import { cn } from '@/common/lib/cn/cn'
import { Portal } from '../Portal/Portal'
import { Icon } from '../Icon/Icon'
import { Button } from '../Form/Button'
import './modal.css'

export type ModalProps = {
    color?: 'primary' | 'transparent'
    actions?: React.ReactNode
    className?: string
    container?: string | HTMLElement
    modalClassName?: string
    showCloseButton?: boolean
    closeOnEscape?: boolean
    /** Controlled open state via DaisyUI `modal-open` class */
    open?: boolean
    onClose?: () => void
} & React.HTMLAttributes<HTMLDialogElement>

export const Modal = ({
    children,
    id,
    color = 'primary',
    className,
    modalClassName,
    container = 'body',
    showCloseButton = true,
    closeOnEscape = true,
    open,
    onClose,
    ...props
}: ModalProps) => {
    const isControlled = open !== undefined
    const onCloseRef = React.useRef(onClose)
    onCloseRef.current = onClose

    const cardClasses = cn(
        'modal',
        {
            '': color === 'primary',
            'bg-transparent': color === 'transparent',
            'modal-open': isControlled && open,
        },
        className
    )

    const handleClose = () => {
        onCloseRef.current?.()
    }

    const handleCloseKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key !== 'Enter' && event.key !== ' ') return
        event.preventDefault()
        handleClose()
    }

    React.useEffect(() => {
        if (!closeOnEscape) return
        if (isControlled && !open) return

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return

            if (isControlled) {
                if (!open) return
                handleClose()
                return
            }

            if (!id) return
            const checkbox = document.getElementById(id) as HTMLInputElement | null
            if (!checkbox?.checked) return
            checkbox.checked = false
        }

        document.addEventListener('keydown', handleKeyDown)
        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [id, closeOnEscape, isControlled, open])

    return (
        <Portal container={container}>
            {!isControlled && <input type='checkbox' id={id} className='modal-toggle' />}
            <dialog role='dialog' className={cardClasses} {...props}>
                <div className={cn('modal-box', modalClassName)}>
                    {showCloseButton &&
                        (isControlled ? (
                            <Button
                                type='button'
                                className='modal-close'
                                emphasis='neutral'
                                variant='ghost'
                                onClick={handleClose}
                                onKeyDown={handleCloseKeyDown}
                                tabIndex={0}
                                aria-label='Закрыть'
                            >
                                <Icon name='cross' size='lg' />
                            </Button>
                        ) : (
                            <Button as='label' htmlFor={id} className='modal-close' emphasis='neutral' variant='ghost'>
                                <Icon name='cross' size='lg' />
                            </Button>
                        ))}
                    {children}
                </div>
                {showCloseButton &&
                    (isControlled ? (
                        <button
                            type='button'
                            className='modal-backdrop'
                            onClick={handleClose}
                            onKeyDown={handleCloseKeyDown}
                            tabIndex={0}
                            aria-label='Закрыть'
                        >
                            Close
                        </button>
                    ) : (
                        <label className='modal-backdrop' htmlFor={id}>
                            Close
                        </label>
                    ))}
            </dialog>
        </Portal>
    )
}
